// Copyright (c) StrangeBee 2024

import React, { ReactElement, useState } from 'react';
import { Space } from 'antd';
import { useLocalMessage } from '@Helpers/';
import moment, { Moment } from 'moment';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import { THButton } from '@Molecules/Inputs/THButton/THButton';
import { DashboardEventList } from '@Organisms/Dashboard/eventList';
import { THRangePicker } from '@Molecules/Inputs/THRangePicker/THRangePicker';
import { DashboardOutput } from 'thehive-sdk';
import { ICONS } from '@Constants/iconsApp';

import './DashboardPeriod.scss';

const PeriodValues = ['all', 'last3Months', 'last30Days', 'last7Days', 'custom', 'today'] as const;
export type PeriodValue = (typeof PeriodValues)[number];
export const isPeriodValue = (period: string): period is PeriodValue => PeriodValues.includes(period as PeriodValue);
export const periods: Record<PeriodValue, { from?: (today: Moment) => Moment }> = {
    all: {},
    last3Months: {
        from: (today: Moment): Moment => moment(today).subtract(3, 'months'),
    },
    last30Days: {
        from: (today: Moment): Moment => moment(today).subtract(30, 'days'),
    },
    last7Days: {
        from: (today: Moment): Moment => moment(today).subtract(7, 'days'),
    },
    today: {
        from: (today: Moment): Moment => moment(today).subtract(1, 'days'),
    },
    custom: {},
};

interface DashboardPeriodProps {
    eventList: DashboardEventList;
    dashboard: DashboardOutput;
    allowPeriodAll: boolean;
}

const DashboardPeriod = ({ eventList, dashboard, allowPeriodAll }: DashboardPeriodProps): ReactElement => {
    const i = useLocalMessage();

    const { fromDate, toDate } = dashboard.definition.customPeriod || {};

    const [period, setPeriod] = useState<PeriodValue>(dashboard.definition.period as PeriodValue);
    const [{ from, to }, setDates] = useState<{ from: Moment | null; to: Moment | null }>({
        from: fromDate ? moment(fromDate) : null,
        to: toDate ? moment(toDate) : null,
    });

    const onChangeHandler = (value: PeriodValue): void => {
        setPeriod(value);

        if (value === 'custom') {
            return;
        }

        onFinishHandler(value);
    };

    const onFinishHandler = (period: PeriodValue, from?: Moment | null, to?: Moment | null): void => {
        if (period === 'custom' && (!from || !to)) {
            return;
        }

        const newFrom =
            from
                ?.hours(from.hours() || 0)
                .minutes(from.minutes() || 0)
                .seconds(from.seconds() || 0)
                .milliseconds(from.milliseconds() || 0)
                .valueOf() || 0;
        const newTo =
            to
                ?.hours(to.hours() || 23)
                .minutes(to.minutes() || 59)
                .seconds(to.seconds() || 59)
                .milliseconds(to.milliseconds() || 999)
                .valueOf() || 0;

        eventList.onEdit(
            {
                definition: {
                    ...dashboard.definition,
                    period,
                    customPeriod:
                        period === 'custom'
                            ? {
                                  fromDate: newFrom,
                                  toDate: newTo,
                              }
                            : undefined,
                },
            },
            dashboard._id,
            false,
        );
    };

    return (
        <Space direction="horizontal" size="large" className="dashboard-period">
            <span className="dashboard-period__title">{i('dashboard.settings.period')}</span>
            <THSelect<PeriodValue>
                defaultValue={allowPeriodAll ? 'last3Months' : 'all'}
                value={period}
                onChange={onChangeHandler}
                className="dashboard-period__select"
            >
                {(Object.keys(periods) as PeriodValue[]).map((period) => (
                    <THSelect.Option key={period} value={period} disabled={allowPeriodAll && period === 'all'}>
                        {i(`dashboard.period.${period}`)}
                    </THSelect.Option>
                ))}
            </THSelect>
            {period === 'custom' && (
                <Space size="large">
                    <THRangePicker
                        value={[from, to]}
                        onChange={(values) => values && setDates({ from: values[0], to: values[1] })}
                        placeholder={[
                            i('app.dashboard.panel.menu.period.custom.from.placeholder'),
                            i('app.dashboard.panel.menu.period.custom.to.placeholder'),
                        ]}
                    />

                    <THButton
                        type="primary"
                        icon={<ICONS.SearchIcon />}
                        onClick={() => onFinishHandler(period, from, to)}
                    >
                        {i('dashboard.widget.period.apply')}
                    </THButton>
                </Space>
            )}
        </Space>
    );
};

export default DashboardPeriod;
